import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Download,
  Filter,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  Package,
  Tag,
  Clock,
  Building2,
  RefreshCw,
} from 'lucide-react';
import {
  useDailySalesReport,
  useOrdersReport,
  usePaymentReport,
  useProductReport,
  useCategoryReport,
  useHourlyReport,
  useBranchReport,
  DatePreset,
  DateRangeParams,
} from '../../api/queries/reports.queries';
import { useBranches } from '../../api/queries/transfer.queries';
import { useAuthStore } from '../../stores/auth.store';

// Report tabs configuration
const REPORT_TABS = [
  { id: 'daily-sales', label: 'Daily Sales', icon: TrendingUp },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'by-payment', label: 'By Payment', icon: CreditCard },
  { id: 'by-product', label: 'By Product', icon: Package },
  { id: 'by-category', label: 'By Category', icon: Tag },
  { id: 'by-hour', label: 'By Hour', icon: Clock },
  { id: 'by-branch', label: 'By Branch', icon: Building2 },
] as const;

type ReportType = typeof REPORT_TABS[number]['id'];

// Date preset options
const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

function SalesReportsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const isHQ = user?.role === 'HQ_ADMIN';

  // Read initial values from URL params
  const initialTab = (searchParams.get('tab') as ReportType) || 'daily-sales';
  const initialPreset = (searchParams.get('preset') as DatePreset) || 'last7days';
  const initialBranch = searchParams.get('branchId') || (isHQ ? 'all' : user?.branchId || '');
  const initialPaymentMethod = searchParams.get('paymentMethod') || '';

  const [activeTab, setActiveTab] = useState<ReportType>(
    REPORT_TABS.some(t => t.id === initialTab) ? initialTab : 'daily-sales'
  );
  const [selectedBranch, setSelectedBranch] = useState<string>(initialBranch);
  const [datePreset, setDatePreset] = useState<DatePreset>(initialPreset);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [orderStatus, setOrderStatus] = useState<string>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>(initialPaymentMethod);

  // Update state when URL params change (e.g., from dashboard navigation)
  useEffect(() => {
    const tab = searchParams.get('tab') as ReportType;
    const preset = searchParams.get('preset') as DatePreset;
    const branchId = searchParams.get('branchId');
    const paymentMethod = searchParams.get('paymentMethod');
    const status = searchParams.get('status');

    if (tab && REPORT_TABS.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
    if (preset && DATE_PRESETS.some(p => p.value === preset)) {
      setDatePreset(preset);
    }
    if (branchId) {
      setSelectedBranch(branchId);
    }
    if (paymentMethod) {
      setPaymentMethodFilter(paymentMethod);
      // Auto-switch to orders tab if payment method filter is set
      if (!tab) setActiveTab('orders');
    }
    if (status) {
      setOrderStatus(status);
      // Auto-switch to orders tab if status filter is set
      if (!tab) setActiveTab('orders');
    }
  }, [searchParams]);

  // Build date params
  const dateParams: DateRangeParams = useMemo(() => {
    if (useCustomDates && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return { preset: datePreset };
  }, [useCustomDates, customStartDate, customEndDate, datePreset]);

  // Fetch branches
  const { data: branches } = useBranches();

  // Fetch reports
  const {
    data: dailySalesData,
    isLoading: isLoadingDailySales,
    refetch: refetchDailySales,
  } = useDailySalesReport(selectedBranch, dateParams);

  const {
    data: ordersData,
    isLoading: isLoadingOrders,
    refetch: refetchOrders,
  } = useOrdersReport(selectedBranch, dateParams, ordersPage, 50, orderStatus || undefined, paymentMethodFilter || undefined);

  const {
    data: paymentData,
    isLoading: isLoadingPayment,
    refetch: refetchPayment,
  } = usePaymentReport(selectedBranch, dateParams);

  const {
    data: productData,
    isLoading: isLoadingProduct,
    refetch: refetchProduct,
  } = useProductReport(selectedBranch, dateParams, 100);

  const {
    data: categoryData,
    isLoading: isLoadingCategory,
    refetch: refetchCategory,
  } = useCategoryReport(selectedBranch, dateParams);

  const {
    data: hourlyData,
    isLoading: isLoadingHourly,
    refetch: refetchHourly,
  } = useHourlyReport(selectedBranch, dateParams);

  const {
    data: branchData,
    isLoading: isLoadingBranch,
    refetch: refetchBranch,
  } = useBranchReport(dateParams);

  // Currency formatter
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Number formatter
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('th-TH').format(num);
  };

  // Date formatter for display (respects current language)
  const formatDateRange = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'th' ? 'th-TH' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get current report loading state
  const isLoading = useMemo(() => {
    switch (activeTab) {
      case 'daily-sales': return isLoadingDailySales;
      case 'orders': return isLoadingOrders;
      case 'by-payment': return isLoadingPayment;
      case 'by-product': return isLoadingProduct;
      case 'by-category': return isLoadingCategory;
      case 'by-hour': return isLoadingHourly;
      case 'by-branch': return isLoadingBranch;
      default: return false;
    }
  }, [activeTab, isLoadingDailySales, isLoadingOrders, isLoadingPayment, isLoadingProduct, isLoadingCategory, isLoadingHourly, isLoadingBranch]);

  // Refresh current report
  const handleRefresh = () => {
    switch (activeTab) {
      case 'daily-sales': refetchDailySales(); break;
      case 'orders': refetchOrders(); break;
      case 'by-payment': refetchPayment(); break;
      case 'by-product': refetchProduct(); break;
      case 'by-category': refetchCategory(); break;
      case 'by-hour': refetchHourly(); break;
      case 'by-branch': refetchBranch(); break;
    }
  };

  // Export to CSV
  const exportToCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((header) => {
          const value = row[header];
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value;
        }).join(',')
      ),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Get export data for current tab
  const handleExportCSV = () => {
    switch (activeTab) {
      case 'daily-sales':
        if (dailySalesData?.rows) {
          exportToCSV(dailySalesData.rows.map(row => ({
            Date: row.date,
            Revenue: row.revenue,
            Orders: row.orders,
            Subtotal: row.subtotal,
            Discount: row.discount,
            VAT: row.vat,
            'Service Charge': row.serviceCharge,
            'Items Sold': row.itemsSold,
            'Avg Order Value': row.avgOrderValue,
          })), 'daily_sales_report');
        }
        break;
      case 'orders':
        if (ordersData?.orders) {
          exportToCSV(ordersData.orders.map(order => ({
            'Order #': order.orderNumber,
            'Receipt #': order.receiptNumber || '-',
            Branch: order.branchName,
            Date: new Date(order.createdAt).toLocaleString('th-TH'),
            Subtotal: order.subtotal,
            Discount: order.discount,
            VAT: order.vat,
            'Service Charge': order.serviceCharge,
            Total: order.total,
            'Payment Method': order.paymentMethod,
            Status: order.status,
            Items: order.itemCount,
          })), 'orders_report');
        }
        break;
      case 'by-payment':
        if (paymentData?.rows) {
          exportToCSV(paymentData.rows.map(row => ({
            'Payment Method': row.paymentMethod,
            Revenue: row.revenue,
            Orders: row.orders,
            'Avg Order Value': row.avgOrderValue,
            'Percentage': `${row.percentage}%`,
          })), 'payment_report');
        }
        break;
      case 'by-product':
        if (productData?.rows) {
          exportToCSV(productData.rows.map(row => ({
            Rank: row.rank,
            Product: row.name,
            Category: row.category,
            Quantity: row.quantity,
            Revenue: row.revenue,
            'Order Count': row.orderCount,
            'Avg Price': row.avgPrice,
            'Revenue %': `${row.revenuePercentage}%`,
          })), 'product_report');
        }
        break;
      case 'by-category':
        if (categoryData?.rows) {
          exportToCSV(categoryData.rows.map(row => ({
            Category: row.name,
            Quantity: row.quantity,
            Revenue: row.revenue,
            'Unique Items': row.uniqueItems,
            'Percentage': `${row.percentage}%`,
          })), 'category_report');
        }
        break;
      case 'by-hour':
        if (hourlyData?.rows) {
          exportToCSV(hourlyData.rows.map(row => ({
            Hour: row.hourLabel,
            Revenue: row.revenue,
            Orders: row.orders,
          })), 'hourly_report');
        }
        break;
      case 'by-branch':
        if (branchData?.rows) {
          exportToCSV(branchData.rows.map(row => ({
            Branch: row.branchName,
            'Branch Code': row.branchCode,
            Revenue: row.revenue,
            Orders: row.orders,
            Discount: row.discount,
            'Items Sold': row.itemsSold,
            'Avg Order Value': row.avgOrderValue,
            'Revenue %': `${row.revenuePercentage}%`,
          })), 'branch_report');
        }
        break;
    }
  };

  // Render report content based on active tab
  const renderReportContent = () => {
    switch (activeTab) {
      case 'daily-sales':
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium">{t('reports.date')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.orders')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.subtotal')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.discount')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.vat')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.serviceCharge')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.itemsSold')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.avgOrderValue')}</th>
                </tr>
              </thead>
              <tbody>
                {dailySalesData?.rows && dailySalesData.rows.length > 0 ? (
                  <>
                    {dailySalesData.rows.map((row, index) => (
                      <tr key={row.date} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium">{row.date}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.orders)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.subtotal)}</td>
                        <td className="py-3 px-4 text-right text-red-500">{formatCurrency(row.discount)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.vat)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.serviceCharge)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.itemsSold)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.avgOrderValue)}</td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 bg-primary/5 font-bold">
                      <td className="py-3 px-4">{t('common.total')}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(dailySalesData.totals.revenue)}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(dailySalesData.totals.orders)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(dailySalesData.totals.subtotal)}</td>
                      <td className="py-3 px-4 text-right text-red-500">{formatCurrency(dailySalesData.totals.discount)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(dailySalesData.totals.vat)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(dailySalesData.totals.serviceCharge)}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(dailySalesData.totals.itemsSold)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(dailySalesData.totals.avgOrderValue)}</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-muted-foreground">
                      {isLoading ? t('common.loading') : t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'orders':
        return (
          <>
            {/* Filters */}
            <div className="mb-4 flex items-center gap-4 flex-wrap">
              {/* Status filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  value={orderStatus}
                  onChange={(e) => { setOrderStatus(e.target.value); setOrdersPage(1); }}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">{t('reports.allStatuses')}</option>
                  <option value="completed">{t('orders.completed')}</option>
                  <option value="cancelled">{t('orders.cancelled')}</option>
                  <option value="voided">{t('orders.voided')}</option>
                </select>
              </div>

              {/* Payment method filter indicator */}
              {paymentMethodFilter && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full text-sm">
                  <CreditCard className="h-3.5 w-3.5" />
                  <span className="capitalize">{paymentMethodFilter}</span>
                  <button
                    onClick={() => {
                      setPaymentMethodFilter('');
                      // Clear URL param
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('paymentMethod');
                      setSearchParams(newParams, { replace: true });
                    }}
                    className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left py-3 px-4 font-medium">{t('orders.orderNumber')}</th>
                    <th className="text-left py-3 px-4 font-medium">{t('orders.branch')}</th>
                    <th className="text-left py-3 px-4 font-medium">{t('reports.date')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('reports.subtotal')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('reports.discount')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('reports.total')}</th>
                    <th className="text-center py-3 px-4 font-medium">{t('reports.paymentMethod')}</th>
                    <th className="text-center py-3 px-4 font-medium">{t('orders.status')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('orders.items')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersData?.orders && ordersData.orders.length > 0 ? (
                    ordersData.orders.map((order, index) => (
                      <tr key={order.id} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium">#{order.orderNumber}</td>
                        <td className="py-3 px-4">{order.branchName}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {new Date(order.createdAt).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="py-3 px-4 text-right">{formatCurrency(order.subtotal)}</td>
                        <td className="py-3 px-4 text-right text-red-500">{formatCurrency(order.discount)}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(order.total)}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 capitalize">
                            {order.paymentMethod}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            order.status === 'completed' || order.status === 'picked_up'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              : order.status === 'cancelled' || order.status === 'voided'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">{order.itemCount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-muted-foreground">
                        {isLoading ? t('common.loading') : t('reports.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination & Summary */}
            {ordersData?.pagination && ordersData.pagination.pages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {t('reports.showingOrders', {
                    from: (ordersData.pagination.page - 1) * ordersData.pagination.limit + 1,
                    to: Math.min(ordersData.pagination.page * ordersData.pagination.limit, ordersData.pagination.total),
                    total: ordersData.pagination.total,
                  }) || `Showing ${(ordersData.pagination.page - 1) * ordersData.pagination.limit + 1} - ${Math.min(ordersData.pagination.page * ordersData.pagination.limit, ordersData.pagination.total)} of ${ordersData.pagination.total}`}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                    disabled={ordersPage === 1}
                    className="px-3 py-1.5 rounded-md border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="px-3 py-1.5 text-sm">
                    {ordersPage} / {ordersData.pagination.pages}
                  </span>
                  <button
                    onClick={() => setOrdersPage((p) => Math.min(ordersData.pagination.pages, p + 1))}
                    disabled={ordersPage === ordersData.pagination.pages}
                    className="px-3 py-1.5 rounded-md border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}

            {/* Summary */}
            {ordersData?.summary && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">{formatCurrency(ordersData.summary.totalRevenue)}</div>
                  <div className="text-xs text-muted-foreground">{t('reports.totalRevenue')}</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold">{formatNumber(ordersData.summary.totalOrders)}</div>
                  <div className="text-xs text-muted-foreground">{t('reports.totalOrders')}</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-red-500">{formatCurrency(ordersData.summary.totalDiscount)}</div>
                  <div className="text-xs text-muted-foreground">{t('reports.totalDiscount')}</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">{formatNumber(ordersData.summary.completedOrders)}</div>
                  <div className="text-xs text-muted-foreground">{t('orders.completed')}</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-red-500">{formatNumber(ordersData.summary.cancelledOrders)}</div>
                  <div className="text-xs text-muted-foreground">{t('orders.cancelled')}</div>
                </div>
              </div>
            )}
          </>
        );

      case 'by-payment':
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium">{t('reports.paymentMethod')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.orders')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.avgOrderValue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {paymentData?.rows && paymentData.rows.length > 0 ? (
                  <>
                    {paymentData.rows.map((row, index) => (
                      <tr key={row.paymentMethod} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium capitalize">{row.paymentMethod}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.orders)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.avgOrderValue)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${row.percentage}%` }}
                              />
                            </div>
                            <span>{row.percentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 bg-primary/5 font-bold">
                      <td className="py-3 px-4">{t('common.total')}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(paymentData.totals.revenue)}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(paymentData.totals.orders)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(paymentData.totals.avgOrderValue)}</td>
                      <td className="py-3 px-4 text-right">100%</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      {isLoading ? t('common.loading') : t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'by-product':
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-center py-3 px-4 font-medium w-16">{t('reports.rank')}</th>
                  <th className="text-left py-3 px-4 font-medium">{t('reports.product')}</th>
                  <th className="text-left py-3 px-4 font-medium">{t('reports.category')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.quantity')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.orderCount')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.avgPrice')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenuePercent')}</th>
                </tr>
              </thead>
              <tbody>
                {productData?.rows && productData.rows.length > 0 ? (
                  <>
                    {productData.rows.map((row, index) => (
                      <tr key={row.id} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            row.rank <= 3 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' : 'bg-muted text-muted-foreground'
                          }`}>
                            {row.rank}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium">{row.name}</td>
                        <td className="py-3 px-4 text-muted-foreground">{row.category}</td>
                        <td className="py-3 px-4 text-right font-semibold">{formatNumber(row.quantity)}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.orderCount)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.avgPrice)}</td>
                        <td className="py-3 px-4 text-right">{row.revenuePercentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 bg-primary/5 font-bold">
                      <td className="py-3 px-4" colSpan={3}>{t('common.total')}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(productData.totals.quantity)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(productData.totals.revenue)}</td>
                      <td className="py-3 px-4" colSpan={3}></td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      {isLoading ? t('common.loading') : t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'by-category':
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium">{t('reports.category')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.quantity')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.uniqueItems')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.percentage')}</th>
                </tr>
              </thead>
              <tbody>
                {categoryData?.rows && categoryData.rows.length > 0 ? (
                  <>
                    {categoryData.rows.map((row, index) => (
                      <tr key={row.id} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium">{row.name}</td>
                        <td className="py-3 px-4 text-right font-semibold">{formatNumber(row.quantity)}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                        <td className="py-3 px-4 text-right">{row.uniqueItems}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${row.percentage}%` }}
                              />
                            </div>
                            <span>{row.percentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 bg-primary/5 font-bold">
                      <td className="py-3 px-4">{t('common.total')}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(categoryData.totals.quantity)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(categoryData.totals.revenue)}</td>
                      <td className="py-3 px-4" colSpan={2}></td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      {isLoading ? t('common.loading') : t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'by-hour':
        return (
          <>
            {/* Peak Hour Highlight */}
            {hourlyData?.peakHour && (
              <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-green-600" />
                  <div>
                    <span className="text-sm text-muted-foreground">{t('reports.peakHour')}:</span>
                    <span className="ml-2 font-bold text-green-700 dark:text-green-400">{hourlyData.peakHour.label}</span>
                    <span className="ml-4 text-sm">
                      {formatCurrency(hourlyData.peakHour.revenue)} / {hourlyData.peakHour.orders} {t('reports.orders')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left py-3 px-4 font-medium">{t('reports.hour')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                    <th className="text-right py-3 px-4 font-medium">{t('reports.orders')}</th>
                    <th className="text-left py-3 px-4 font-medium w-1/3">{t('reports.distribution')}</th>
                  </tr>
                </thead>
                <tbody>
                  {hourlyData?.rows && hourlyData.rows.length > 0 ? (
                    <>
                      {hourlyData.rows.map((row, index) => {
                        const maxRevenue = Math.max(...hourlyData.rows.map((r) => r.revenue));
                        const percentage = maxRevenue > 0 ? (row.revenue / maxRevenue) * 100 : 0;
                        const isPeak = hourlyData.peakHour?.hour === row.hour;

                        return (
                          <tr key={row.hour} className={`border-b ${isPeak ? 'bg-green-50 dark:bg-green-900/20' : index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                            <td className="py-3 px-4 font-medium">{row.hourLabel}</td>
                            <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                            <td className="py-3 px-4 text-right">{formatNumber(row.orders)}</td>
                            <td className="py-3 px-4">
                              <div className="h-4 bg-secondary rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${isPeak ? 'bg-green-500' : 'bg-primary'}`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {/* Totals Row */}
                      <tr className="border-t-2 bg-primary/5 font-bold">
                        <td className="py-3 px-4">{t('common.total')}</td>
                        <td className="py-3 px-4 text-right text-green-600">{formatCurrency(hourlyData.totals.revenue)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(hourlyData.totals.orders)}</td>
                        <td className="py-3 px-4"></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        {isLoading ? t('common.loading') : t('reports.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        );

      case 'by-branch':
        if (!isHQ) {
          return (
            <div className="py-8 text-center text-muted-foreground">
              {t('reports.branchReportHQOnly')}
            </div>
          );
        }

        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium">{t('reports.branch')}</th>
                  <th className="text-left py-3 px-4 font-medium">{t('reports.branchCode')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.orders')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.discount')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.itemsSold')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.avgOrderValue')}</th>
                  <th className="text-right py-3 px-4 font-medium">{t('reports.revenuePercent')}</th>
                </tr>
              </thead>
              <tbody>
                {branchData?.rows && branchData.rows.length > 0 ? (
                  <>
                    {branchData.rows.map((row, index) => (
                      <tr key={row.branchId} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium">{row.branchName}</td>
                        <td className="py-3 px-4 text-muted-foreground">{row.branchCode}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(row.revenue)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.orders)}</td>
                        <td className="py-3 px-4 text-right text-red-500">{formatCurrency(row.discount)}</td>
                        <td className="py-3 px-4 text-right">{formatNumber(row.itemsSold)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(row.avgOrderValue)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${row.revenuePercentage}%` }}
                              />
                            </div>
                            <span>{row.revenuePercentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="border-t-2 bg-primary/5 font-bold">
                      <td className="py-3 px-4" colSpan={2}>{t('common.total')}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(branchData.totals.revenue)}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(branchData.totals.orders)}</td>
                      <td className="py-3 px-4 text-right text-red-500">{formatCurrency(branchData.totals.discount)}</td>
                      <td className="py-3 px-4 text-right">{formatNumber(branchData.totals.itemsSold)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(branchData.totals.avgOrderValue)}</td>
                      <td className="py-3 px-4 text-right">100%</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      {isLoading ? t('common.loading') : t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('reports.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('reports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-9 w-9 rounded-md border bg-background hover:bg-secondary flex items-center justify-center disabled:opacity-50"
            title={t('common.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Export CSV */}
          <button
            onClick={handleExportCSV}
            className="h-9 px-3 rounded-md border bg-background hover:bg-secondary flex items-center gap-2 text-sm"
            title={t('reports.exportCSV')}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-card rounded-lg border p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Branch Filter (HQ only) */}
          {isHQ && branches && activeTab !== 'by-branch' && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm min-w-[180px]"
              >
                <option value="all">{t('dashboard.allBranches')}</option>
                {branches.map((branch: any) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range */}
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="h-4 w-4 text-muted-foreground" />

            {/* Preset Dropdown */}
            <select
              value={useCustomDates ? 'custom' : datePreset}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setUseCustomDates(true);
                } else {
                  setUseCustomDates(false);
                  setDatePreset(e.target.value as DatePreset);
                }
              }}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              {DATE_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {t(`reports.presets.${preset.value}`) || preset.label}
                </option>
              ))}
              <option value="custom">{t('reports.customRange')}</option>
            </select>

            {/* Custom Date Inputs */}
            {useCustomDates && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                />
                <span className="text-muted-foreground">-</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                />
              </>
            )}
          </div>

          {/* Date Range Display */}
          {dailySalesData?.dateRange && (
            <div className="ml-auto text-sm text-muted-foreground whitespace-nowrap">
              {formatDateRange(dailySalesData.dateRange.start)} - {formatDateRange(dailySalesData.dateRange.end)}
            </div>
          )}
        </div>
      </div>

      {/* Report Tabs */}
      <div className="bg-card rounded-lg border">
        {/* Tab Navigation */}
        <div className="border-b overflow-x-auto overflow-y-hidden">
          <nav className="flex">
            {REPORT_TABS.map((tab) => {
              // Hide branch tab for non-HQ users
              if (tab.id === 'by-branch' && !isHQ) return null;

              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t(`reports.tabs.${tab.id}`) || tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Report Content */}
        <div className="p-4">
          {renderReportContent()}
        </div>
      </div>
    </div>
  );
}

export default SalesReportsPage;
